import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Trash2, FileText } from 'lucide-react';
import { Pill } from './ui/Pill';
import { useConfirm } from './ui/ConfirmDialog';
import { api } from '../lib/api';

async function openFormFile(wsId: string, formId: string): Promise<void> {
  const r = await api.get(`/workspaces/${wsId}/tax-us/forms/${formId}/file`, { responseType: 'blob' });
  const url = URL.createObjectURL(r.data as Blob);
  window.open(url, '_blank', 'noopener,noreferrer');
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

type FormKind = 'W-9' | 'W-8BEN' | 'W-8BEN-E' | 'W-8ECI' | 'W-8EXP';
type FormStatus = 'on_file' | 'pending' | 'expired' | 'revoked';
const KINDS: FormKind[] = ['W-9', 'W-8BEN', 'W-8BEN-E', 'W-8ECI', 'W-8EXP'];
const STATUS_TONE: Record<FormStatus, 'success' | 'accent' | 'warn' | 'muted'> = {
  on_file: 'success', pending: 'accent', expired: 'warn', revoked: 'muted',
};

interface TaxForm {
  id: string;
  form_kind: FormKind;
  tax_year: number;
  country_code: string | null;
  file_url: string | null;
  signed_at: string | null;
  expires_on: string | null;
  status: FormStatus;
}

const wsKey = (wsId: string, vendorId: string) => ['vendor-tax-forms', wsId, vendorId];

const inputCn =
  'w-full px-2 py-1.5 text-sm rounded-md border border-ink-300 dark:border-ink-700 ' +
  'bg-white dark:bg-ink-900 focus:outline-none focus:ring-2 focus:ring-accent-500';

/** W-9 / W-8BEN-E collection on the vendor record (tax-us module). */
export function VendorTaxForms({ wsId, vendorId }: { wsId: string; vendorId: string }) {
  const { t } = useTranslation();
  const confirm = useConfirm();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [formKind, setFormKind] = useState<FormKind>('W-9');
  const [taxYear, setTaxYear] = useState(new Date().getFullYear());
  const [tin, setTin] = useState('');
  const [countryCode, setCountryCode] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [error, setError] = useState<string | null>(null);

  const forms = useQuery<TaxForm[]>({
    queryKey: wsKey(wsId, vendorId),
    queryFn: async () => (await api.get<TaxForm[]>(`/workspaces/${wsId}/tax-us/vendor/${vendorId}/forms`)).data,
  });

  const reset = () => { setOpen(false); setTin(''); setFile(null); setError(null); };

  const upsert = useMutation({
    mutationFn: async () => {
      if (file) {
        // Multipart upload of the actual W-9/W-8 document.
        const fd = new FormData();
        fd.append('file', file);
        fd.append('formKind', formKind);
        fd.append('taxYear', String(taxYear));
        if (tin) fd.append('tin', tin);
        if (countryCode) fd.append('countryCode', countryCode);
        return (await api.post(`/workspaces/${wsId}/tax-us/vendor/${vendorId}/forms/upload`, fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
        })).data;
      }
      return (await api.post(`/workspaces/${wsId}/tax-us/vendor/${vendorId}/forms`, {
        formKind, taxYear, tin: tin || undefined, countryCode: countryCode || undefined,
      })).data;
    },
    onSuccess: () => { reset(); qc.invalidateQueries({ queryKey: wsKey(wsId, vendorId) }); },
    onError: (err: any) => setError(err?.response?.data?.message ?? err?.message ?? 'Failed'),
  });

  const del = useMutation({
    mutationFn: async (id: string) => (await api.delete(`/workspaces/${wsId}/tax-us/vendor/${vendorId}/forms/${id}`)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['vendor-tax-forms', wsId, vendorId] }),
  });

  return (
    <div className="col-span-2">
      <div className="flex items-center justify-between mb-2">
        <span className="text-[11px] uppercase tracking-[0.16em] text-ink-500">{t('vendors.taxForms.title')}</span>
        <button type="button" onClick={() => setOpen((o) => !o)} className="inline-flex items-center gap-1 text-xs text-accent-600 hover:text-accent-700">
          <Plus size={12} /> {t('vendors.taxForms.add')}
        </button>
      </div>

      {open && (
        <div className="mb-3 p-2 rounded-lg border border-ink-200 dark:border-ink-800 bg-ink-50 dark:bg-ink-900/40 space-y-2">
          <div className="grid grid-cols-2 gap-2">
            <select value={formKind} onChange={(e) => setFormKind(e.target.value as FormKind)} className={inputCn}>
              {KINDS.map((k) => <option key={k} value={k}>{k}</option>)}
            </select>
            <input type="number" value={taxYear} onChange={(e) => setTaxYear(Number(e.target.value))} className={inputCn} placeholder={t('vendors.taxForms.year')} />
            <input value={tin} onChange={(e) => setTin(e.target.value)} className={inputCn} placeholder={t('vendors.taxForms.tin')} />
            <input value={countryCode} onChange={(e) => setCountryCode(e.target.value.toUpperCase().slice(0, 2))} className={inputCn} placeholder={t('vendors.taxForms.country')} />
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-wide text-ink-500 block mb-1">{t('vendors.taxForms.document')}</label>
            <input type="file" accept=".pdf,image/*" onChange={(e) => setFile(e.target.files?.[0] ?? null)} className="w-full text-xs" />
            {file && <div className="text-[11px] text-ink-500 mt-1 truncate">{file.name} · {(file.size / 1024).toFixed(0)} KB</div>}
          </div>
          {error && <div className="text-xs text-warn-600">{error}</div>}
          <button type="button" onClick={() => upsert.mutate()} disabled={upsert.isPending} className="px-3 py-1.5 rounded-lg bg-accent-600 text-white text-xs font-medium hover:bg-accent-700 disabled:opacity-50">
            {t('common.save')}
          </button>
        </div>
      )}

      {forms.isLoading ? (
        <div className="text-xs text-ink-500">{t('common.loading')}</div>
      ) : !forms.data?.length ? (
        <div className="text-xs text-ink-500">{t('vendors.taxForms.none')}</div>
      ) : (
        <ul className="space-y-1">
          {forms.data.map((f) => (
            <li key={f.id} className="flex items-center justify-between text-sm rounded-md border border-ink-200 dark:border-ink-800 px-2 py-1.5">
              <span className="num">{f.form_kind} · {f.tax_year}{f.country_code ? ` · ${f.country_code}` : ''}</span>
              <div className="flex items-center gap-2">
                {f.file_url && (
                  <button type="button" onClick={() => openFormFile(wsId, f.id)} className="p-1 rounded text-accent-600 hover:text-accent-700" title={t('vendors.taxForms.viewDoc')}>
                    <FileText size={13} />
                  </button>
                )}
                <Pill tone={STATUS_TONE[f.status]}>{t(`vendors.taxForms.status.${f.status}`)}</Pill>
                <button
                  type="button"
                  onClick={async () => { if (await confirm({ title: t('common.confirmDelete'), confirmLabel: t('common.delete'), destructive: true })) del.mutate(f.id); }}
                  className="p-1 rounded text-ink-400 hover:text-warn-600"
                  title={t('common.delete')}
                >
                  <Trash2 size={13} />
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
